import {
  useEffect,
  useRef,
  useState,
} from 'react'

type EnterpriseRecaptchaApi = {
  render: (
    container: HTMLElement,
    options: Record<
      string,
      unknown
    >,
  ) => number

  reset: (
    widgetId?: number,
  ) => void
}

type RecaptchaWindow =
  Window & {
    grecaptcha?: {
      enterprise?:
        EnterpriseRecaptchaApi
    }

    __diamondSalonRecaptchaLoaded?:
      () => void
  }

let scriptPromise:
  | Promise<EnterpriseRecaptchaApi>
  | null = null

const SCRIPT_ID =
  'diamond-salon-enterprise-recaptcha'

function currentApi() {
  return (
    window as
      RecaptchaWindow
  ).grecaptcha
    ?.enterprise
}

function waitForApi(
  timeoutMs = 10_000,
): Promise<EnterpriseRecaptchaApi> {
  return new Promise<
    EnterpriseRecaptchaApi
  >(
    (
      resolve,
      reject,
    ) => {
      const startedAt =
        Date.now()

      const check = () => {
        const api =
          currentApi()

        if (api?.render) {
          resolve(api)
          return
        }

        if (
          Date.now() -
            startedAt >=
          timeoutMs
        ) {
          reject(
            new Error(
              'reCAPTCHA Enterprise did not initialize.',
            ),
          )

          return
        }

        window.setTimeout(
          check,
          50,
        )
      }

      check()
    },
  )
}

function loadRecaptcha():
  Promise<EnterpriseRecaptchaApi> {
  const api =
    currentApi()

  if (api?.render) {
    return Promise.resolve(
      api,
    )
  }

  if (scriptPromise) {
    return scriptPromise
  }

  const loader =
    new Promise<
      EnterpriseRecaptchaApi
    >(
      (
        resolve,
        reject,
      ) => {
        const recaptchaWindow =
          window as
            RecaptchaWindow

        const finish =
          () => {
            waitForApi()
              .then(resolve)
              .catch(reject)
          }

        recaptchaWindow
          .__diamondSalonRecaptchaLoaded =
          finish

        const existing =
          document.getElementById(
            SCRIPT_ID,
          )

        if (existing) {
          finish()
          return
        }

        const script =
          document.createElement(
            'script',
          )

        script.id =
          SCRIPT_ID

        script.src =
          'https://www.google.com/recaptcha/enterprise.js?onload=__diamondSalonRecaptchaLoaded&render=explicit'

        script.async =
          true

        script.defer =
          true

        script.onerror =
          () => {
            script.remove()

            reject(
              new Error(
                'reCAPTCHA Enterprise could not be loaded.',
              ),
            )
          }

        document.head
          .appendChild(
            script,
          )
      },
    ).catch(
      (error) => {
        scriptPromise =
          null

        throw error
      },
    )

  scriptPromise =
    loader

  return loader
}

type Props = {
  onTokenChange:
    (token: string) =>
      void

  resetKey:
    number

  theme?:
    | 'light'
    | 'dark'
}

export function Recaptcha({
  onTokenChange,
  resetKey,
  theme = 'light',
}: Props) {
  const containerRef =
    useRef<HTMLDivElement>(
      null,
    )

  const widgetRef =
    useRef<number | null>(
      null,
    )

  const callbackRef =
    useRef(
      onTokenChange,
    )

  const previousResetKey =
    useRef(
      resetKey,
    )

  const [
    error,
    setError,
  ] =
    useState('')

  const siteKey =
    import.meta.env
      .VITE_RECAPTCHA_SITE_KEY
      ?.trim() ?? ''

  useEffect(() => {
    callbackRef.current =
      onTokenChange
  }, [
    onTokenChange,
  ])

  useEffect(() => {
    callbackRef.current(
      '',
    )

    setError('')

    if (
      !siteKey ||
      !containerRef.current
    ) {
      return
    }

    let cancelled =
      false

    loadRecaptcha()
      .then(
        (api) => {
          if (
            cancelled ||
            !containerRef.current ||
            widgetRef.current !==
              null
          ) {
            return
          }

          widgetRef.current =
            api.render(
              containerRef.current,
              {
                sitekey:
                  siteKey,

                theme,

                callback:
                  (
                    token:
                      string,
                  ) => {
                    if (
                      cancelled
                    ) {
                      return
                    }

                    setError(
                      '',
                    )

                    callbackRef
                      .current(
                        token,
                      )
                  },

                'expired-callback':
                  () => {
                    callbackRef
                      .current(
                        '',
                      )
                  },

                'error-callback':
                  () => {
                    callbackRef
                      .current(
                        '',
                      )

                    setError(
                      'Security verification could not be completed. Please try again.',
                    )
                  },
              },
            )
        },
      )
      .catch(
        (
          loadError,
        ) => {
          console.error(
            'reCAPTCHA Enterprise load error:',
            loadError,
          )

          if (
            cancelled
          ) {
            return
          }

          setError(
            'Security verification could not be loaded. Please refresh and try again.',
          )
        },
      )

    return () => {
      cancelled =
        true
    }
  }, [
    siteKey,
    theme,
  ])

  useEffect(() => {
    if (
      previousResetKey
        .current ===
      resetKey
    ) {
      return
    }

    previousResetKey.current =
      resetKey

    callbackRef.current(
      '',
    )

    setError('')

    const api =
      currentApi()

    if (
      api &&
      widgetRef.current !==
        null
    ) {
      try {
        api.reset(
          widgetRef.current,
        )
      } catch (
        resetError
      ) {
        console.error(
          'reCAPTCHA Enterprise reset error:',
          resetError,
        )
      }
    }
  }, [
    resetKey,
  ])

  if (!siteKey) {
    return (
      <p
        className="recaptcha-notice recaptcha-notice--error"
        role="alert"
      >
        Human verification is
        temporarily unavailable.
      </p>
    )
  }

  return (
    <div className="recaptcha-wrap">
      <div
        ref={
          containerRef
        }
      />

      {error && (
        <p
          className="recaptcha-notice recaptcha-notice--error"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  )
}